import { ChevronRight, Calendar, Clock, CheckCircle2, FileText, Sparkles, AlertTriangle } from "lucide-react";
import { format, differenceInDays, isPast } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { getDemarcheTemplate, DEMARCHE_CATEGORIES } from "@/config/demarchesConfig";

interface Demarche {
  id: string;
  title: string;
  description: string | null;
  template_key: string | null;
  status: string;
  due_date: string | null;
  auto_generated: boolean;
  documents_count?: number;
  documents_completed?: number;
  steps_count?: number;
  steps_completed?: number;
}

interface DemarcheCardProps {
  demarche: Demarche;
  onClick: () => void;
}

const STATUS_STYLES = {
  pending: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    dot: 'bg-amber-500',
    text: 'text-amber-600 dark:text-amber-400',
  },
  in_progress: {
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
    dot: 'bg-blue-500',
    text: 'text-blue-600 dark:text-blue-400',
  },
  completed: {
    bg: 'bg-green-500/10',
    border: 'border-green-500/20',
    dot: 'bg-green-500',
    text: 'text-green-600 dark:text-green-400',
  },
  cancelled: {
    bg: 'bg-muted',
    border: 'border-border',
    dot: 'bg-muted-foreground',
    text: 'text-muted-foreground',
  },
  overdue: {
    bg: 'bg-destructive/10',
    border: 'border-destructive/30',
    dot: 'bg-destructive animate-pulse',
    text: 'text-destructive',
  },
};

export function DemarcheCard({ demarche, onClick }: DemarcheCardProps) {
  const template = demarche.template_key ? getDemarcheTemplate(demarche.template_key) : null;
  const category = template ? DEMARCHE_CATEGORIES[template.category] : null;
  
  // Check if overdue (computed state)
  const dueDate = demarche.due_date ? new Date(demarche.due_date) : null;
  const isOverdue = dueDate && isPast(dueDate) && demarche.status !== 'completed';
  
  // Use overdue styles if applicable, otherwise use status styles
  const styles = isOverdue 
    ? STATUS_STYLES.overdue 
    : (STATUS_STYLES[demarche.status as keyof typeof STATUS_STYLES] || STATUS_STYLES.pending);

  // Progress
  const totalItems = (demarche.documents_count || 0) + (demarche.steps_count || 0);
  const completedItems = (demarche.documents_completed || 0) + (demarche.steps_completed || 0);
  const progressPercent = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  // Due date display
  const daysUntilDue = dueDate ? differenceInDays(dueDate, new Date()) : null;

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left p-4 rounded-xl border transition-all relative overflow-hidden",
        "hover:shadow-md hover:scale-[1.01] active:scale-[0.99]",
        styles.bg,
        styles.border,
        isOverdue && "ring-1 ring-destructive/50 shadow-[0_0_15px_-3px] shadow-destructive/20"
      )}
    >
      {/* Animated border glow for overdue */}
      {isOverdue && (
        <div className="absolute inset-0 rounded-xl animate-pulse bg-gradient-to-r from-destructive/5 via-destructive/10 to-destructive/5 pointer-events-none" />
      )}
      <div className="flex items-start gap-3 relative z-10">
        {/* Status indicator */}
        <div className={cn("w-2.5 h-2.5 rounded-full mt-1.5 shrink-0", styles.dot)} />

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {isOverdue && (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0 gap-0.5">
                <AlertTriangle className="w-2.5 h-2.5" />
                En retard
              </Badge>
            )}
            {category && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                {category.label}
              </Badge>
            )}
            {demarche.auto_generated && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 gap-0.5">
                <Sparkles className="w-2.5 h-2.5" />
                Auto
              </Badge>
            )}
          </div>

          <h3 className="font-medium text-sm truncate mb-1">{demarche.title}</h3>

          {/* Progress bar */}
          {totalItems > 0 && (
            <div className="mb-2">
              <Progress value={progressPercent} className="h-1.5" />
              <div className="flex items-center justify-between mt-1">
                <span className="text-[10px] text-muted-foreground">
                  {completedItems}/{totalItems} éléments
                </span>
                <span className="text-[10px] font-medium">{progressPercent}%</span>
              </div>
            </div>
          )}

          {/* Due date */}
          <div className="flex items-center gap-3 text-xs">
            {dueDate && (
              <span className={cn(
                "flex items-center gap-1",
                isOverdue ? "text-destructive" : "text-muted-foreground"
              )}>
                {isOverdue ? (
                  <>
                    <AlertTriangle className="w-3 h-3" />
                    En retard
                  </>
                ) : daysUntilDue !== null && daysUntilDue <= 7 ? (
                  <>
                    <Clock className="w-3 h-3" />
                    {daysUntilDue === 0 ? "Aujourd'hui" : `${daysUntilDue}j`}
                  </>
                ) : (
                  <>
                    <Calendar className="w-3 h-3" />
                    {format(dueDate, 'd MMM', { locale: fr })}
                  </>
                )}
              </span>
            )}

            {(demarche.documents_count || 0) > 0 && (
              <span className="flex items-center gap-1 text-muted-foreground">
                <FileText className="w-3 h-3" />
                {demarche.documents_completed}/{demarche.documents_count}
              </span>
            )}

            {demarche.status === 'completed' && (
              <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                <CheckCircle2 className="w-3 h-3" />
                Terminée
              </span>
            )}
          </div>
        </div>

        {/* Arrow */}
        <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
      </div>
    </button>
  );
}
